// frontend/src/components/TenantPortal/ConnectDataPanel.jsx
// Phase 2 — "Connect Your Data" slide-over panel
// Shown to Tenant Admin (r4) when they click "Data Settings" in the App Gallery.
// Zero impact on non-admin users or existing platform dashboards.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  X, Database, CheckCircle2, AlertCircle, Loader2,
  ChevronDown, ChevronRight, RefreshCw, Unlink, Info,
  ArrowRightLeft, Plug,
} from 'lucide-react';
import { apiFetch } from '../../api';
import { useAuth } from '../../context/useAuth';
import { useTimezoneFormatter } from '../../hooks/useTimezoneFormatter';

// ─── Status badge config ────────────────────────────────────────────────────
const STATUS_CONFIG = {
  READY: {
    icon: <CheckCircle2 className="w-4 h-4" />,
    label: 'Your Data',
    cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  },
  REFRESHING: {
    icon: <Loader2 className="w-4 h-4 animate-spin" />,
    label: 'Syncing…',
    cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  },
  FAILED: {
    icon: <AlertCircle className="w-4 h-4" />,
    label: 'Sync Failed',
    cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  },
  PENDING: {
    icon: <Database className="w-4 h-4" />,
    label: 'Platform Data',
    cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  },
};

// ─── Individual model card ───────────────────────────────────────────────────
function ModelCard({ model, connections, onRefreshStatus }) {
  const { token } = useAuth();
  const { formatDate } = useTimezoneFormatter();
  const [expanded, setExpanded] = useState(false);
  const [selectedConn, setSelectedConn] = useState(model.override?.connection_id || '');
  const [mapping, setMapping] = useState(model.override?.schema_mapping || {});
  const [showMapping, setShowMapping] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(model.override?.data_status === 'REFRESHING');
  const [status, setStatus] = useState(model.override?.data_status || 'PENDING');
  const [lastRefreshed, setLastRefreshed] = useState(model.override?.last_refreshed_at);
  const [lastError, setLastError] = useState(model.override?.last_error);
  const [showError, setShowError] = useState(false);
  const [confirmRevert, setConfirmRevert] = useState(false);
  const pollRef = useRef(null);

  // ── Poll while syncing ──────────────────────────────────────────────────
  const poll = useCallback(async () => {
    try {
      const res = await apiFetch(
        `/tenant-portal/data-connect/overrides/${model.model_id}`,
        token,
      );
      const s = res.data_status || res.status || 'PENDING';
      setStatus(s);
      setLastRefreshed(res.last_refreshed_at);
      setLastError(res.last_error);
      if (s !== 'REFRESHING') {
        clearInterval(pollRef.current);
        setSyncing(false);
        onRefreshStatus?.();
      }
    } catch (_) { /* non-fatal */ }
  }, [model.model_id, token, onRefreshStatus]);

  useEffect(() => {
    if (syncing) {
      pollRef.current = setInterval(poll, 3000);
    }
    return () => clearInterval(pollRef.current);
  }, [syncing, poll]);

  // ── Save config ─────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      await apiFetch(
        `/tenant-portal/data-connect/overrides/${model.model_id}`,
        token,
        {
          method: 'PUT',
          body: JSON.stringify({
            connection_id: selectedConn,
            schema_mapping: Object.keys(mapping).length > 0 ? mapping : null,
          }),
        },
      );
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  // ── Trigger sync ─────────────────────────────────────────────────────────
  const handleSync = async () => {
    if (!selectedConn) return;
    await handleSave(); // persist config first
    setSyncing(true);
    setStatus('REFRESHING');
    setLastError(null);
    try {
      await apiFetch(
        `/tenant-portal/data-connect/overrides/${model.model_id}/sync`,
        token,
        { method: 'POST' },
      );
    } catch (err) {
      setSyncing(false);
      setStatus('FAILED');
      setLastError(String(err));
    }
  };

  // ── Revert to platform data ────────────────────────────────────────
  const handleRevert = async () => {
    try {
      await apiFetch(
        `/tenant-portal/data-connect/overrides/${model.model_id}`,
        token,
        { method: 'DELETE' },
      );
      setStatus('PENDING');
      setLastRefreshed(null);
      setConfirmRevert(false);
      onRefreshStatus?.();
    } catch (err) {
      console.error('Revert failed:', err);
      setConfirmRevert(false);
    }
  };

  const badge = STATUS_CONFIG[status] || STATUS_CONFIG.PENDING;

  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
      {/* Card header */}
      <button
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-750 transition-colors text-left"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="flex items-center gap-3 min-w-0">
          <Database className="w-4 h-4 text-slate-400 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
              {model.model_name}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {model.tables.length} table{model.tables.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0 ml-2">
          {/* Status badge */}
          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${badge.cls}`}>
            {badge.icon}
            {badge.label}
          </span>
          {expanded
            ? <ChevronDown className="w-4 h-4 text-slate-400" />
            : <ChevronRight className="w-4 h-4 text-slate-400" />
          }
        </div>
      </button>

      {/* Expanded config */}
      {expanded && (
        <div className="px-4 py-4 space-y-4 bg-white dark:bg-slate-900">

          {/* Error banner */}
          {status === 'FAILED' && lastError && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <strong>Last sync failed.</strong>
                {!showError && (
                  <button
                    className="ml-2 underline text-red-600 dark:text-red-400"
                    onClick={() => setShowError(true)}
                  >
                    View Error →
                  </button>
                )}
                {showError && (
                  <pre className="mt-2 text-xs whitespace-pre-wrap font-mono bg-red-100 dark:bg-red-900/30 p-2 rounded">
                    {lastError}
                  </pre>
                )}
                <p className="mt-1 text-xs text-red-500">Dashboards are currently showing platform sample data.</p>
              </div>
            </div>
          )}

          {/* Last refreshed */}
          {status === 'READY' && lastRefreshed && (
            <div className="flex items-center gap-2 text-xs text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Last synced: {formatDate(lastRefreshed)}
            </div>
          )}

          {/* Tables list */}
          <div>
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
              Tables in this model
            </p>
            <div className="flex flex-wrap gap-1.5">
              {model.tables.map(t => (
                <span
                  key={t.name}
                  className="text-xs px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-mono"
                >
                  {t.name}
                </span>
              ))}
            </div>
          </div>

          {/* Step 1: Connection */}
          <div>
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
              Step 1 — Select Datasource Connection
            </label>
            <select
              value={selectedConn}
              onChange={e => setSelectedConn(e.target.value)}
              className="w-full text-sm border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-500"
            >
              <option value="">— Select a connection —</option>
              {connections.map(c => (
                <option key={c.id} value={c.id}>{c.name || c.label || c.id}</option>
              ))}
            </select>
            {connections.length === 0 && (
              <p className="mt-1 text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                <Info className="w-3 h-3" />
                No connections found. Add one in <strong>Settings → Connections</strong>.
              </p>
            )}
          </div>

          {/* Step 2: Optional schema mapping */}
          <div>
            <button
              className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
              onClick={() => setShowMapping(m => !m)}
            >
              <ArrowRightLeft className="w-3.5 h-3.5" />
              Step 2 — Column / Table Mapping
              <span className="ml-1 text-slate-400">(optional)</span>
              {showMapping ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            </button>

            {showMapping && (
              <div className="mt-3 space-y-3">
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  If your table or column names differ from the platform schema, enter the mapping below.
                  Leave blank to use platform names as-is.
                </p>
                {model.tables.map(t => (
                  <TableMappingRow
                    key={t.name}
                    platformTable={t.name}
                    columns={t.columns}
                    mapping={mapping[t.name] || {}}
                    onChange={tblMapping =>
                      setMapping(prev => ({ ...prev, [t.name]: tblMapping }))
                    }
                  />
                ))}
              </div>
            )}
          </div>

          {/* Step 3: Actions */}
          <div className="pt-2 border-t border-slate-100 dark:border-slate-800 space-y-2">
            {/* Inline revert confirmation */}
            {confirmRevert ? (
              <div className="flex items-center gap-2 p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                <span className="text-xs text-amber-700 dark:text-amber-300 flex-1">
                  Revert to platform sample data?
                </span>
                <button
                  onClick={handleRevert}
                  className="text-xs px-2.5 py-1 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors font-medium"
                >
                  Confirm
                </button>
                <button
                  onClick={() => setConfirmRevert(false)}
                  className="text-xs px-2.5 py-1 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-md transition-colors"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setConfirmRevert(true)}
                  disabled={status === 'PENDING'}
                  className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 hover:text-red-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <Unlink className="w-3.5 h-3.5" />
                  Revert to Platform Data
                </button>

                <button
                  onClick={handleSync}
                  disabled={!selectedConn || syncing}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg
                             bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700
                             disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                >
                  {syncing
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Syncing…</>
                    : <><RefreshCw className="w-4 h-4" /> Sync Now</>
                  }
                </button>
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
}

// ─── Per-table mapping row (optional step 2) ─────────────────────────────────
function TableMappingRow({ platformTable, columns, mapping, onChange }) {
  const [tableAlt, setTableAlt] = useState(mapping.tenant_table || '');
  const [colMap, setColMap] = useState(mapping.columns || {});

  const update = (newTable, newCols) => {
    const result = {};
    if (newTable) result.tenant_table = newTable;
    if (Object.keys(newCols).length > 0) result.columns = newCols;
    onChange(Object.keys(result).length > 0 ? result : {});
  };

  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 space-y-2">
      {/* Table rename */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-500 w-24 shrink-0 font-mono">{platformTable}</span>
        <ArrowRightLeft className="w-3 h-3 text-slate-300" />
        <input
          type="text"
          placeholder={`Your table (e.g. ${platformTable}_master)`}
          value={tableAlt}
          onChange={e => {
            setTableAlt(e.target.value);
            update(e.target.value, colMap);
          }}
          className="flex-1 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded px-2 py-1 font-mono"
        />
      </div>
      {/* Column renames */}
      {columns.length > 0 && (
        <details className="group">
          <summary className="text-xs text-slate-400 cursor-pointer list-none flex items-center gap-1">
            <ChevronRight className="w-3 h-3 group-open:rotate-90 transition-transform" />
            Map columns ({columns.length})
          </summary>
          <div className="mt-2 space-y-1 pl-4">
            {columns.map(col => (
              <div key={col} className="flex items-center gap-2">
                <span className="text-xs text-slate-500 w-32 shrink-0 font-mono truncate">{col}</span>
                <ArrowRightLeft className="w-3 h-3 text-slate-300 shrink-0" />
                <input
                  type="text"
                  placeholder={col}
                  value={colMap[col] || ''}
                  onChange={e => {
                    const nc = { ...colMap, [col]: e.target.value };
                    if (!e.target.value) delete nc[col];
                    setColMap(nc);
                    update(tableAlt, nc);
                  }}
                  className="flex-1 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded px-2 py-1 font-mono"
                />
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

// ─── Main panel ─────────────────────────────────────────────────────────────
export default function ConnectDataPanel({ bundleId, appName, onClose }) {
  const { token } = useAuth();
  const [models, setModels] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      console.log('[DataConnect] loadData called — bundleId:', bundleId);
      const [modelsRes, connsRes] = await Promise.all([
        apiFetch(`/tenant-portal/data-connect/bundles/${bundleId}/models`, token),
        apiFetch('/datasources', token),
      ]);
      console.log('[DataConnect] modelsRes:', modelsRes);
      console.log('[DataConnect] connsRes:', connsRes);
      setModels(Array.isArray(modelsRes) ? modelsRes : []);
      setConnections(Array.isArray(connsRes) ? connsRes : []);
      setError(null);
    } catch (err) {
      setError('Failed to load data settings.');
      console.error('[DataConnect] loadData error:', err);
    } finally {
      setLoading(false);
    }
  }, [bundleId, token]);

  useEffect(() => {
    loadData();
  }, [loadData, refreshKey]);

  const totalReady = models.filter(m => m.override?.data_status === 'READY').length;
  const totalModels = models.length;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100]"
        onClick={onClose}
      />

      {/* Slide-over panel */}
      <div className="fixed right-0 top-0 h-full w-full max-w-xl bg-white dark:bg-slate-900 shadow-2xl z-[101] flex flex-col animate-slide-in-right">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
              <Database className="w-4 h-4 text-violet-600 dark:text-violet-400" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">
                Data Settings
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">{appName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress summary */}
        {!loading && totalModels > 0 && (
          <div className="px-6 py-3 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {totalReady} of {totalModels} model{totalModels !== 1 ? 's' : ''} connected
              </span>
              <span className="text-xs font-semibold text-violet-600 dark:text-violet-400">
                {totalModels > 0 ? Math.round((totalReady / totalModels) * 100) : 0}%
              </span>
            </div>
            <div className="w-full h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-violet-500 to-emerald-500 rounded-full transition-all duration-500"
                style={{ width: `${totalModels > 0 ? (totalReady / totalModels) * 100 : 0}%` }}
              />
            </div>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {loading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          {!loading && !error && models.length === 0 && (
            <div className="text-center py-16">
              <Plug className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">
                No data models found for this app.
              </p>
              <p className="text-xs text-slate-400 mt-1">
                Dashboards in this bundle may not be using shared data models.
              </p>
            </div>
          )}

          {!loading && models.map(model => (
            <ModelCard
              key={model.model_id}
              model={model}
              connections={connections}
              onRefreshStatus={() => setRefreshKey(k => k + 1)}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
          <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
            <Info className="w-3.5 h-3.5 shrink-0" />
            Dashboards automatically update once sync is complete — no reload needed.
          </p>
        </div>
      </div>

      {/* Slide-in animation */}
      <style>{`
        @keyframes slide-in-right {
          from { transform: translateX(100%); }
          to   { transform: translateX(0); }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        }
      `}</style>
    </>
  );
}
